import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { getInitials } from '../../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Search, Loader2, Check } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NewChatModal = ({ open, onClose, onChatCreated }) => {
  const { token, language } = useAuth();
  const tr = t(language);
  
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);

  const handleSearch = async (value) => {
    setQuery(value);
    if (value.length < 2) {
      setUsers([]);
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(`${API}/users/search?q=${encodeURIComponent(value)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectUser = async (selectedUser) => {
    setCreating(true);
    try {
      const response = await axios.post(`${API}/chats`, {
        participant_ids: [selectedUser.id],
        is_group: false
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      onChatCreated(response.data);
    } catch (error) {
      console.error('Failed to create chat:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setQuery('');
    setUsers([]);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="new-chat-modal">
        <DialogHeader>
          <DialogTitle>{tr('newChat')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder={tr('searchUsers')}
              value={query}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10 h-12 rounded-2xl bg-secondary/30"
              data-testid="user-search-input"
            />
          </div>

          {/* Results */}
          <div className="max-h-[300px] overflow-y-auto space-y-1">
            {searching && (
              <div className="flex justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            )}

            {!searching && query.length >= 2 && users.length === 0 && (
              <p className="text-center py-4 text-muted-foreground">{tr('noResults')}</p>
            )}

            {users.map(user => (
              <button
                key={user.id}
                onClick={() => handleSelectUser(user)}
                disabled={creating}
                className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-secondary/50 transition-colors"
                data-testid={`user-option-${user.id}`}
              >
                <Avatar className="w-12 h-12">
                  <AvatarImage src={user.avatar} alt={user.display_name} />
                  <AvatarFallback className="bg-primary/10 text-primary">
                    {getInitials(user.display_name || user.username)}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1 text-left min-w-0">
                  <p className="font-semibold truncate">{user.display_name || user.username}</p>
                  <p className="text-sm text-muted-foreground truncate">@{user.username}</p>
                </div>

                {user.is_following && (
                  <span className="text-xs text-primary flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    {tr('following')}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewChatModal;
